import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Mail, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import posthog from 'posthog-js'
import { blocage, etape } from '@/lib/analytics/inscription'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { GoogleIcon } from '@/components/icons/GoogleIcon'
import { TurnstileWidget } from '@/components/TurnstileWidget'
import { ApiHttpError, registerAccount } from '@/api/auth'
import { API_BASE_URL } from '@/lib/config'
import { marketingUrl } from '@/lib/marketing'
import { defaultLocale } from '@/lib/locales'
import { poserReceptionniste } from '@/lib/receptionniste'
import { capturer } from '@/lib/numeroSouhaite'
import {
  enParametres as provenanceEnParametres,
  lire as lireProvenance,
  oublier as oublierProvenance,
} from '@/lib/provenance'

// Messages dans la langue de la page : ils étaient en français pour tous.
function creerSchema(t: TFunction) {
  return z
    .object({
      email: z.string().min(1, t('auth.signup.emailRequired')).email(t('auth.signup.emailInvalid')),
      password: z.string().min(8, t('auth.signup.passwordTooShort')),
      confirm: z.string().min(1, t('auth.signup.confirmRequired')),
      agree: z.literal(true, {
        errorMap: () => ({ message: t('auth.signup.acceptTerms') }),
      }),
      offers: z.boolean().optional(),
    })
    .refine((data) => data.password === data.confirm, {
      path: ['confirm'],
      message: t('auth.signup.passwordMismatch'),
    })
}

type SignupForm = z.infer<ReturnType<typeof creerSchema>>

import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function SignupPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  useDocumentTitle(t('signup.pageTitle', 'Inscription'))

  // Un visiteur venu d'une page `/numero-local/<indicatif>` arrive avec le
  // numéro qu'il a cliqué dans l'URL. Le choix du numéro, lui, n'a lieu qu'à
  // l'étape 1, après création du compte et vérification du courriel : on met
  // donc l'intention de côté ici, sinon elle meurt au premier rechargement.
  useEffect(() => {
    capturer(location.search)
    // Même logique pour la réceptionniste IA choisie sur la page Tarifs :
    // gardée en cookie jusqu'au paiement.
    const slug = new URLSearchParams(location.search).get('receptionist')
    if (slug) poserReceptionniste(slug)
  }, [location.search])

  const [turnstileToken, setTurnstileToken] = useState('')
  // Un jeton Turnstile ne sert qu'une fois : après un refus du serveur, on en
  // redemande un. Sans ça, le deuxième essai était refusé (« Token already
  // used », 23 oct 2026) et il fallait recharger la page, formulaire vidé.
  const [reinitTurnstile, setReinitTurnstile] = useState(0)
  const nouveauJeton = () => {
    setTurnstileToken('')
    setReinitTurnstile((n) => n + 1)
  }
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

  // Repere 1 : la personne a ouvert le formulaire. C'est le denominateur de
  // tout le reste (43 ouvertures pour 10 inscriptions, releve du 22 sept 2026).
  useEffect(() => {
    etape('formulaire_ouvert', { appareil: window.innerWidth < 768 ? 'mobile' : 'ordinateur' })
  }, [])

  // Repere 2 : Turnstile a rendu son jeton. Sans ce repere, un echec du test
  // anti-robot est indiscernable d'un abandon volontaire.
  useEffect(() => {
    if (turnstileToken) etape('turnstile_vu')
  }, [turnstileToken])

  /** Cf. Login.tsx — pose `oauth_frontend_url` en session Django avant le
   *  redirect Google pour que le callback renvoie vers app.callizy.ca. */
  const startGoogleAuth = async () => {
    localStorage.setItem('preferred_locale', locale)
    try {
      await fetch(
        `${API_BASE_URL}/api/auth/oauth-init/?frontend_url=${encodeURIComponent(
          window.location.origin,
        )}${provenanceEnParametres()}`,
        { credentials: 'include' },
      )
    } catch {
      /* fallback Referer/Origin côté backend */
    }
    window.location.href = `${API_BASE_URL}/accounts/google/login/?locale=${locale}`
  }

  const signupSchema = useMemo(() => creerSchema(t), [t])
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: '', password: '', confirm: '', agree: false as unknown as true, offers: false },
  })

  const signupMutation = useMutation({
    mutationFn: async (values: SignupForm) => {
      etape('formulaire_envoye')
      if (!turnstileToken) {
        blocage('formulaire_envoye', 'turnstile_manquant')
        throw new Error('TURNSTILE_REQUIRED')
      }
      const provenance = lireProvenance()
      const res = await registerAccount({
        email: values.email,
        password1: values.password,
        password2: values.confirm,
        turnstile_token: turnstileToken,
        locale,
        consentement_offres: !!values.offers,
        provenance,
      })
      oublierProvenance()
      try {
        // The account id, like after any sign-in: the server sends signup_completed
        // for this same person, with the origin set once on the person
        // (programme_partenaires/suivi), so neither is sent here. PostHog's cookie
        // is on .callizy.ca: this identify also ties the visit of callizy.ca.
        posthog.identify(
          (res.user?.pk ?? res.user?.id)?.toString() || values.email.toLowerCase(),
          { email: values.email.toLowerCase() },
        )
        etape('compte_cree')
      } catch {
        // PostHog non initialisé — non bloquant
      }
      return values.email.toLowerCase()
    },
    onSuccess: (normalizedEmail) => {
      navigate(`/${locale}/verify-email?email=${encodeURIComponent(normalizedEmail)}`, {
        replace: true,
      })
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'TURNSTILE_REQUIRED') {
        toast.error(t('toasts.signup.robotCheckRequired'))
        return
      }
      // Le jeton a peut-être été vérifié, donc consommé : on en redemande un.
      nouveauJeton()
      if (error instanceof ApiHttpError) {
        // Le code du champ refuse, jamais le message : il change avec la langue.
        const d = error.data as Record<string, unknown>
        // Refus du test anti-robot : le serveur répond en anglais technique.
        if (typeof d?.error === 'string' && d.error.startsWith('Turnstile')) {
          blocage('formulaire_envoye', 'turnstile_refuse')
          toast.error(t('toasts.signup.robotCheckRetry'))
          return
        }
        blocage('formulaire_envoye',
          Array.isArray(d?.email) ? 'courriel_refuse'
          : Array.isArray(d?.password1) ? 'mot_de_passe_refuse'
          : `http_${error.status}`)
        const data = error.data as Record<string, unknown>
        const message =
          (Array.isArray(data?.email) && (data.email as string[])[0]) ||
          (Array.isArray(data?.password1) && (data.password1 as string[])[0]) ||
          (Array.isArray(data?.non_field_errors) && (data.non_field_errors as string[])[0]) ||
          (typeof data?.detail === 'string' && data.detail) ||
          t('auth.signup.signupError') ||
          'Erreur lors de la création du compte'
        toast.error(message as string)
        return
      }
      console.error('[Signup]', error)
      toast.error(t('auth.signup.serverError', 'Erreur de connexion au serveur'))
    },
  })

  const onSubmit = handleSubmit((values) => signupMutation.mutate(values))
  const isSubmitting = signupMutation.isPending

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white text-slate-900">
      <div className="relative hidden md:flex items-end bg-[#0A192F] text-white p-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(800px 400px at 20% 10%, #ffffff 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />{' '}
            {t('auth.signup.quickStart', 'Démarrage en 3 minutes')}
          </div>
          <h1 className="text-3xl font-semibold leading-tight">
            {t('auth.signup.heroTitle', 'Créez votre compte')}
          </h1>
          <p className="text-white/80 max-w-md">
            {t('auth.signup.heroDescription') ||
              'Numéro local canadien, routage agents, messagerie vocale par e-mail. Essai gratuit 14 jours.'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-6 flex items-center gap-2">
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="h-7 w-7"
            />
            <span className="font-semibold">Callizy</span>
          </div>

          <h2 className="text-2xl font-semibold">{t('auth.signup.title')}</h2>
          <p className="text-slate-600 text-sm mt-1">{t('auth.signup.subtitle')}</p>

          <button
            type="button"
            onClick={() => void startGoogleAuth()}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-900 hover:bg-slate-50"
          >
            <GoogleIcon className="h-5 w-5" />
            {t('auth.signup.google')}
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-500">{t('common.or', 'ou')}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email-s" className="text-slate-700">
                {t('auth.signup.email')}
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email-s"
                  type="email"
                  className="pl-8"
                  placeholder="vous@entreprise.ca"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                  disabled={isSubmitting}
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pwd-s" className="text-slate-700">
                  {t('auth.signup.password')}
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="pwd-s"
                    type="password"
                    className="pl-8"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    aria-invalid={!!errors.password}
                    disabled={isSubmitting}
                    {...register('password')}
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="pwd2-s" className="text-slate-700">
                  {t('auth.signup.confirmPassword')}
                </Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="pwd2-s"
                    type="password"
                    className="pl-8"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    aria-invalid={!!errors.confirm}
                    disabled={isSubmitting}
                    {...register('confirm')}
                  />
                </div>
                {errors.confirm && (
                  <p className="mt-1 text-xs text-red-600">{errors.confirm.message}</p>
                )}
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                className="mt-1"
                disabled={isSubmitting}
                {...register('agree')}
              />
              <span>
                {t('auth.signup.acceptTermsText')}{' '}
                <a
                  href={marketingUrl('/legal/terms', locale)}
                  className="underline hover:text-slate-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('auth.signup.termsLink')}
                </a>{' '}
                {t('auth.signup.and')}{' '}
                <a
                  href={marketingUrl('/legal/privacy', locale)}
                  className="underline hover:text-slate-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('auth.signup.privacyLink')}
                </a>
                .
              </span>
            </label>
            {errors.agree && (
              <p className="text-xs text-red-600">{errors.agree.message}</p>
            )}

            <label className="flex items-start gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                className="mt-1"
                disabled={isSubmitting}
                {...register('offers')}
              />
              <span>{t('auth.signup.offersConsent')}</span>
            </label>

            {turnstileSiteKey ? (
              <div className="flex justify-center">
                <TurnstileWidget
                  siteKey={turnstileSiteKey}
                  reinitialiser={reinitTurnstile}
                  onVerify={setTurnstileToken}
                  onError={() =>
                    toast.error(t('toasts.signup.robotCheckError'))
                  }
                  theme="auto"
                />
              </div>
            ) : (
              <p className="text-xs text-amber-600">
                VITE_TURNSTILE_SITE_KEY manquant - ajoutez-le dans .env.local pour activer la
                vérification anti-bot.
              </p>
            )}

            <Button
              type="submit"
              className="w-full rounded-xl bg-black text-white py-2.5 font-medium hover:opacity-90 disabled:opacity-50"
              disabled={isSubmitting || (!!turnstileSiteKey && !turnstileToken)}
            >
              {isSubmitting ? t('common.loading') : t('auth.signup.button')}
            </Button>
          </form>

          <p className="mt-4 text-sm text-slate-600">
            {t('auth.signup.hasAccount')}{' '}
            <Link
              to={`/${locale}/login`}
              className="text-[#0A192F] font-medium hover:underline"
            >
              {t('auth.signup.loginLink')}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  )
}
